import { QUICKBOOKS_BRAND_COLOUR } from '@ankor-io/common/types/integrations/quickbooks'
import { XERO_BRAND_COLOR } from '@ankor-io/common/types/integrations/xero/types'
import type { CategoryPayload } from '@ankor-io/common/types/transaction/category'
import { CategoryType } from '@ankor-io/common/types/transaction/category'
import { Badge } from '@ankor-io/ui-components/src/Badge'
import { Chip } from '@ankor-io/ui-components/src/Chip'

/**
 * Returns the display label for a category.
 * When the category has metadata with an account (e.g. Xero-synced), shows `metadata.account.code name`.
 * Otherwise returns the category name.
 *
 * @param category - The category payload
 * @returns Display label string
 */
const getCategoryDisplayLabel = (category: Pick<CategoryPayload, 'name' | 'metadata'>): string => {
  const account = category.metadata?.account
  if (account?.code != null && account?.name != null) {
    return `${account.code} ${account.name}`
  }
  return category.name
}

type CategoryLabelProps = {
  category: Pick<CategoryPayload, 'name' | 'metadata' | 'color' | 'icon' | 'type'>
}

/**
 * Renders a category as either a Badge or Chip based on type and color:
 * - USER => Chip
 * - XERO && color === XERO_BRAND_COLOR => Badge (default Xero styling)
 * - QUICKBOOKS && color === QUICKBOOKS_BRAND_COLOUR => Badge (default QuickBooks styling)
 * - XERO or QUICKBOOKS with any other color => Chip
 *
 * For XERO and QUICKBOOKS categories, the label shows metadata.account.code + name when available.
 */
export const CategoryLabel = ({ category }: CategoryLabelProps) => {
  const label = getCategoryDisplayLabel(category)
  const isXeroWithBrandColor = category.type === CategoryType.XERO && category.color === XERO_BRAND_COLOR
  const isQuickBooksWithBrandColor =
    category.type === CategoryType.QUICKBOOKS && category.color === QUICKBOOKS_BRAND_COLOUR

  if (isXeroWithBrandColor || isQuickBooksWithBrandColor) {
    return (
      <Badge color={isQuickBooksWithBrandColor ? 'quickbooks' : 'xero'}>
        {category.icon && <span>{category.icon}</span>}
        {label}
      </Badge>
    )
  }

  return (
    <Chip
      name={label}
      colour={category.color}
      icon={category.icon}
    />
  )
}
